export const mapNextLocaleToContentfulLocaleMarket = (
  locale: string,
): [ReturnType<typeof mapNextToContentfulLocaleMarket>, ReturnType<typeof mapNextToContentfulMarket>] => [
  mapNextToContentfulLocaleMarket(locale),
  mapNextToContentfulMarket(locale),
];

export const mapNextToContentfulLocaleMarket = (locale: string) => {
  switch (locale) {
    case 'fr':
      return 'fr-BE';
    case 'nl':
      return 'nl-BE';
    case 'nl-NL':
      return 'nl-NL';
    default:
      return 'nl-BE';
  }
};

export const mapNextToContentfulMarket = (locale: string) => {
  switch (locale) {
    case 'fr':
      return 'BE';
    case 'nl':
      return 'BE';
    case 'nl-NL':
      return 'NL';
    default:
      return 'BE';
  }
};

export const mapContentfulToNextLocale = (locale: string) => {
  switch (locale) {
    case 'fr-BE':
      return 'fr';
    case 'nl-BE':
      return 'nl';
    case 'nl-NL':
      return 'nl-NL';
    default:
      return locale;
  }
};

/**
 * Maps Contentful locale codes to market codes
 */
export const mapContentfulLocaleToMarket = (locale: string) => {
  switch (locale) {
    case 'nl-NL':
      return 'NL';
    case 'nl-BE':
      return 'BE';
    case 'fr-BE':
      return 'BE';
    default:
      return 'BE';
  }
};

/**
 * Determines the market based on host
 * @param host - The request host (e.g., 'colora.be', 'colora.nl')
 * @returns The market code ('BE' or 'NL')
 */
export const getMarketFromHost = (host?: string): 'BE' | 'NL' => {
  // Check if on Colora NL domain
  return host === process.env.NEXT_PUBLIC_HOST_COLORA_NL ? 'NL' : 'BE';
};

/**
 * Check if content is published for the given locale (old format)
 * @param locale - The locale (e.g., 'nl', 'fr', 'nl-BE', 'fr-BE')
 * @param publishedForLocales - Array of locales the content is published for
 * @returns true if content is published for the given locale
 */
const isPublishedForOldLocaleFormat = (locale: string, publishedForLocales?: (string | null)[] | null): boolean => {
  if (!publishedForLocales?.length) {
    return false;
  }

  const isNlLocale = locale === 'nl-BE' || locale === 'nl';
  const isFrLocale = locale === 'fr-BE' || locale === 'fr';

  if (isNlLocale && publishedForLocales.includes('nl')) {
    return true;
  }

  if (isFrLocale && publishedForLocales.includes('fr')) {
    return true;
  }

  return publishedForLocales.includes(locale);
};

/**
 * Check if content is published for the current market (new format)
 * @param publishedForMarket - Array of markets the content is published for
 * @param host - Optional: the request host to determine current market
 * @returns true if content is published for the market
 */
const isPublishedForMarket = (publishedForMarket?: (string | null)[] | null, host?: string): boolean => {
  if (!publishedForMarket?.length) {
    return false;
  }

  // If host is provided, check if content is published for the current market
  if (host) {
    const currentMarket = getMarketFromHost(host);

    return publishedForMarket.includes(currentMarket as never);
  }

  // If host is not provided, just check if the field has any values
  // This is used at build time when host context is not available
  return true;
};

/**
 * Check if content is published for the given locale
 * @param locale - The locale (e.g., 'nl', 'fr', 'nl-BE', 'fr-BE')
 * @param publishedForLocales - Array of locales the content is published for
 * @param publishedForMarket - Array of markets the content is published for
 * @param host - Optional: the request host to determine current market
 * @returns true if content should be visible for the locale and market
 */
export const isContentPublishedForLocale = (
  locale: string,
  publishedForLocales?: (string | null)[] | null,
  publishedForMarket?: (string | null)[] | null,
  host?: string,
): boolean => {
  // Check publishedForLocales (supports old locale format)
  if (isPublishedForOldLocaleFormat(locale, publishedForLocales)) {
    return true;
  }

  // Check publishedForMarket (new market-based format)
  if (isPublishedForMarket(publishedForMarket, host)) {
    return true;
  }

  // If no publish fields are set, content is published to all
  return !publishedForLocales?.length && !publishedForMarket?.length;
};
